// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.BubbleChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import DefaultChartOptionsBuilder from './Izenda.DefaultChartOptionsBuilder';

import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';
import {
  getOr,
  nth,
  get,
  merge,
  first,
  compact,
  filter,
  toNumber,
  has,
  forEach,
  isNil,
  flow,
  groupBy,
  map,
  toPairs,
  flattenDeep,
  isEmpty,
  cloneDeep,
  find,
  uniq
} from 'lodash/fp';

import { exportClass } from 'share/Izenda.ServiceProvider';
import ProcessDataUtils from 'share/utils/Izenda.ProcessDataUtils';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';

const X_AXIS_CONTAINER_NAME = 'labels';
const Y_AXIS_CONTAINER_NAME = 'values';
const Z_AXIS_CONTAINER_NAME = 'bubbleSize';
const NAME_CONTAINER_NAME = 'valuesLabels';

@exportClass('BubbleChartOptionsBuilder')
export default class BubbleChartOptionsBuilder extends DefaultChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, userOptions, dataParser): Object {
    const optionsBuilder = this;
    const xAxisContainerName = getOr(X_AXIS_CONTAINER_NAME, 'X_AXIS_CONTAINER_NAME', userOptions);
    const containers = dataParser.containers || {};
    const izendaSeriesConfig = userOptions.izendaSeriesConfig || {};
    const defaultColors = this.DefaultColors;

    const xAxisField = nth(dataParser.expandedLevel, containers[xAxisContainerName]);
    const yAxisField = nth(0, containers[Y_AXIS_CONTAINER_NAME]);
    const yAxisOptions = getYAxisOption();
    const xAxisOptions = getXAxisOption();

    const series = getSeries(userOptions);
    const axisCat = getAxisCategories(series);

    const chartOptions = {
      series: series,
      yAxis: { labels: { formatter: HighchartUtils.getFormattedLabelAxis }, plotLines: getYThresholdOption(series) },
      xAxis: { labels: { formatter: HighchartUtils.getFormattedLabelAxis }, plotLines: getXThresholdOption() }
    };

    // Disable colorByPoint because we handle multi-colors manually.
    const plotOptions = { series: { colorByPoint: false } };

    chartOptions.plotOptions = merge(chartOptions.plotOptions, plotOptions);

    chartOptions.yAxis = merge(chartOptions.yAxis, yAxisOptions);
    chartOptions.xAxis = merge(chartOptions.xAxis, xAxisOptions);

    HighchartUtils.setDefaultAxisName(chartOptions, dataParser);
    this.configureRegression(izendaSeriesConfig, series);

    return $izenda.extend(true, chartOptions, axisCat);

    /**
     * Get x-axis option
     */
    function getXAxisOption() {
      const commonXYAxis = get('commonXYAxis.xAxis', userOptions);
      const xAxisOption = merge(get(xAxisField.fieldName + '.axisOption', izendaSeriesConfig), commonXYAxis);

      return HighchartUtils.setTickIntervalForXAxis(xAxisOption, get('field.dataFieldType', xAxisField));
    }

    /**
     * Get x-threshold option
     */
    function getXThresholdOption() {
      return get(xAxisField.fieldName + '.threshold', izendaSeriesConfig);
    }

    // Get Y-axis options needed when user change chart's metric, by comparing Y-axis's name with metric's name.
    function getYAxisOption() {
      const commonXYAxis = get('commonXYAxis', userOptions);
      const fieldName = get('fieldName', yAxisField);
      const option = get(fieldName + '.axisOption', izendaSeriesConfig);
      return merge(option, get('yAxis', commonXYAxis));
    }

    /**
     * Get y-threshold option
     */
    function getYThresholdOption() {
      const fieldName = get('fieldName', yAxisField);
      return get(fieldName + '.threshold', izendaSeriesConfig);
    }

    /**
     * Get axis categories
     * @param {any} series
     */
    function getAxisCategories(series: any) {
      const firstSeries = first(series);
      const axisCategories = {};

      addConfigToAxis('x');
      addConfigToAxis('y');

      return axisCategories;

      function addConfigToAxis(name) {
        const cat = get(name + 'Categories', firstSeries);
        const axisName = name + 'Axis';
        const axisConfig = (axisCategories[axisName] = axisCategories[axisName] || {});

        axisConfig.type = firstSeries[name + 'AxisType'];

        if (cat && cat.length) {
          axisConfig.categories = cat;
          axisConfig.tickPositioner = HighchartUtils.getCategoriesTicksPositioner();
        }
      }
    }

    /**
     * Get series
     * @param {any} userOptions
     */
    function getSeries(userOptions: any) {
      const rawData = dataParser.rawData;
      const containers = dataParser.containers;
      const xCategories = { obj: {}, arr: [] };
      const yCategories = { obj: {}, arr: [] };

      const series = (rawData.length ? rawData : [rawData]).map(tuple =>
        getSerie(tuple.fieldsMapping, tuple.records, xCategories, yCategories, userOptions)
      );

      return compact(flattenDeep(series));

      function getSerie(fieldsMapping, records, xCategories, yCategories, userOptions) {
        if (!fieldsMapping || !records) {
          return null;
        }

        const dataStructure = ProcessDataUtils.restructureField(containers, fieldsMapping) || {};
        const xAxisFieldMapping = nth(dataParser.expandedLevel, dataStructure[X_AXIS_CONTAINER_NAME]) || {};
        const yAxisFieldMapping = nth(0, dataStructure[Y_AXIS_CONTAINER_NAME]) || {};
        const nameFieldMapping = nth(0, dataStructure[NAME_CONTAINER_NAME]) || {};
        const bubbleSize = nth(0, dataStructure[Z_AXIS_CONTAINER_NAME]) || {};

        let xAxisType = get('type', HighchartUtils.getAxisType(xAxisFieldMapping));
        const yAxisType = get('type', HighchartUtils.getAxisType(yAxisFieldMapping));

        const xAxisConfig = get(xAxisFieldMapping.fieldNameAlias, izendaSeriesConfig);
        const yAxisConfig = get(yAxisFieldMapping.fieldNameAlias, izendaSeriesConfig);

        let point, xRawData, yRawData;

        if (!nameFieldMapping || !nameFieldMapping.columnName) {
          return null;
        }

        // Check category option
        if (xAxisType !== 'category' && !!xAxisOptions.izCategory) {
          xAxisType = 'category';
        }

        const {
          plotOptions: {
            series: { colorByPoint }
          },
          izDisableMultiColor
        } = userOptions;

        const isMultiColor = colorByPoint && !izDisableMultiColor;

        records = filterRecordsDatetimeAxis(records, xAxisFieldMapping, xAxisType);
        records = filterRecordsDatetimeAxis(records, yAxisFieldMapping, yAxisType);

        let serieData = records.map(record => {
          xRawData = ReportPartUtils.getOriginalValue(xAxisFieldMapping, record);
          yRawData = ReportPartUtils.getOriginalValue(yAxisFieldMapping, record);
          const originalNameValue = ReportPartUtils.getOriginalValue(nameFieldMapping, record);
          const size = ReportPartUtils.getOriginalValue(bubbleSize, record);

          point = {
            x: optionsBuilder.getAxisData(xRawData, xCategories, xAxisType, xAxisConfig),
            xRawData,
            y: optionsBuilder.getAxisData(yRawData, yCategories, yAxisType, yAxisConfig),
            yRawData,
            z: size,
            name: get(nameFieldMapping.columnName, record),
            originalNameValue,
            record
          };

          return point;
        });

        if (xAxisType !== 'category') {
          serieData = filter(point => !isNil(point.x) && !isNil(point.y), serieData);
        }
        const params = [
          nameFieldMapping,
          bubbleSize,
          xAxisFieldMapping,
          xAxisType,
          xCategories,
          yAxisFieldMapping,
          yAxisType,
          yCategories,
          userOptions,
          izendaSeriesConfig
        ];

        return isMultiColor && serieData.length > 0
          ? optionsBuilder.parseMultiColorSeriesData(userOptions, defaultColors, serieData, params)
          : [optionsBuilder.getSerieOption(nameFieldMapping.fieldNameAlias, serieData, ...params)];

        function filterRecordsDatetimeAxis(records, axisFieldMapping, axisType) {
          return axisType === 'datetime'
            ? filter(record => !!ReportPartUtils.getOriginalValue(axisFieldMapping, record), records)
            : records;
        }
      }
    }
  }

  /**
   *
   * @param {any} val The axis raw data
   * @param {Object} categoriesInfo The categories info
   * @param {string} axisType Axis type, e.g. linear, datetime or category
   * @param {Object} axisConfig The axis configuration info
   */
  getAxisData(val: any, categoriesInfo: Object, axisType: string, axisConfig: Object) {
    switch (axisType) {
      case 'linear': {
        return toNumber(val);
      }

      case 'datetime': {
        val = convertToDateTimeFormat(get('fieldFormatData', axisConfig), val, false);
        return val.getTime ? val.getTime() : val;
      }

      default: {
        const obj = (categoriesInfo.obj = categoriesInfo.obj || {});
        const arr = (categoriesInfo.arr = categoriesInfo.arr || []);

        if (has(val, obj)) {
          return obj[val];
        } else {
          obj[val] = arr.length;
          arr.push(val);
          return arr.length - 1;
        }
      }
    }
  }

  /**
   * Get serie options
   */
  getSerieOption(
    name,
    serieData,
    nameFieldMapping,
    bubbleSize,
    xAxisFieldMapping,
    xAxisType,
    xCategories,
    yAxisFieldMapping,
    yAxisType,
    yCategories,
    userOptions,
    izendaSeriesConfig
  ) {
    const serieOption = {
      name: name,
      data: serieData,
      xFieldName: xAxisFieldMapping.fieldNameAlias,
      yFieldName: yAxisFieldMapping.fieldNameAlias,
      xCategories: xCategories.arr,
      yCategories: yCategories.arr,
      xAxisType: xAxisType,
      yAxisType: yAxisType,
      xAxisField: xAxisFieldMapping,
      yAxisField: yAxisFieldMapping,
      zAxisField: bubbleSize,
      nameField: nameFieldMapping,
      nameFieldName: nameFieldMapping.fieldNameAlias,
      bubbleFieldName: bubbleSize.fieldNameAlias,
      dataLabels: { useHTML: true, formatter: this.getDataLabelFormat }
    };

    // Add sub-report to data-labels
    HighchartUtils.addPointActionLink(
      true,
      userOptions,
      serieOption.dataLabels,
      nameFieldMapping.fieldNameAlias,
      get(nameFieldMapping.fieldNameAlias, izendaSeriesConfig)
    );

    return serieOption;
  }

  /**
   * Get data label format
   */
  getDataLabelFormat() {
    const formattedValue = HighchartUtils.getFormattedDataByPoint('name', this.point, true);
    return get('data', formattedValue);
  }

  /**
   * Parse multi colors series' data
   * @param {Object} userOptions
   * @param {Array<Object>} defaultColors
   * @param {Array<Object>} serieData
   * @param {Object} params
   * @returns {Array<Object>}
   */
  parseMultiColorSeriesData(
    userOptions: any,
    defaultColors: Array<any>,
    serieData: Array<any>,
    params: any
  ): Array<any> {
    const optionsBuilder = this;
    const colors = isNil(userOptions.colors) ? defaultColors : userOptions.colors;
    const names = flow(
      map(x => (!isNil(x.name) ? x.name.toString() : 'null')),
      uniq
    )(serieData);

    const groupedSeries = flow(
      groupBy('name'),
      toPairs,
      map(({ 0: key, 1: value }) => {
        return { key, value: optionsBuilder.getSerieOption(key, value, ...params) };
      })
    )(serieData);

    return map((name, index) => {
      const serieValue = find(item => item.key === name, groupedSeries).value;
      forEach(v => (v.color = colors[(index + 1) % colors.length]), serieValue.data);
      return serieValue;
    }, names);
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartChart/Izenda.BubbleChartOptionsBuilder.js